import { useQuery } from '@apollo/client/react';
import { useLingui } from '@lingui/react/macro';
import { isDefined } from 'app-shared/utils';
import { Button } from 'app-ui/primitives/input';
import { SKIP_ERROR_TOAST_CONTEXT } from '@/apollo/constants/SkipErrorToastContext';
import { getUserFriendlyErrorMessage } from '@/apollo/utils/getUserFriendlyErrorMessage';
import { hasGraphqlErrorCode } from '@/apollo/utils/hasGraphqlErrorCode';
import { InterventionForm } from '@/intervention/components/InterventionForm';
import { useHasPermission } from '@/session/hooks/useHasPermission';
import { EmptyState } from '@/ui/feedback/empty-state/components/EmptyState';
import { LoadingState } from '@/ui/feedback/loading-state/components/LoadingState';
import { GetInterventionDocument } from '~/generated/graphql';
type InterventionPanelProps = {
  interventionId?: string;
  forcedReadOnly?: boolean;
};
export const InterventionPanel = ({
  interventionId,
  forcedReadOnly = false,
}: InterventionPanelProps) => {
  const { t } = useLingui();
  const canEdit = useHasPermission('org:planning:edit') && !forcedReadOnly;
  const { data, loading, error, refetch } = useQuery(GetInterventionDocument, {
    variables: { id: interventionId ?? '' },
    skip: !isDefined(interventionId),
    fetchPolicy: 'network-only',
    context: SKIP_ERROR_TOAST_CONTEXT,
  });
  if (isDefined(interventionId) && !isDefined(data)) {
    if (loading) return <LoadingState />;
    return (
      <EmptyState
        role="alert"
        title={
          hasGraphqlErrorCode(error, 'NOT_FOUND')
            ? t`Intervention not found`
            : t`Couldn't load this intervention`
        }
        description={
          getUserFriendlyErrorMessage(error) ??
          t`The server didn't answer. Check your connection, then try again.`
        }
        action={
          <Button
            onClick={() => {
              refetch().catch(() => undefined);
            }}
          >{t`Try again`}</Button>
        }
      />
    );
  }
  if (!isDefined(interventionId) && !canEdit)
    return <p>{t`You can view interventions but not change them.`}</p>;
  return (
    <InterventionForm intervention={data?.intervention} isReadOnly={!canEdit} />
  );
};
